import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { InView } from 'react-intersection-observer';
import {
  VenueListSkeleton, CollectionHeadline, Link, getTranslation, Spacer, TranslationProvider,
} from '@resy/ui-kitchen';
import Logger from '../../../../../ui-kitchen/src/utils/logger';

import EventList from '../../components/EventList';
import VenueList from '../../components/VenueList';
import './RGAListContainer.scss';
import { ALL_DAY } from '../../../../../ui-kitchen/src/constants/resyCore';
import { selectIsGlobalDiningAccess } from '../../../../../ui-kitchen/src/reducers/userSlice';

const GDA_TYPE_ID = 10;
const EVENT_CARD_LIMIT = 6;

const rgaListContainerPropTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func.isRequired,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  collectionList: PropTypes.arrayOf(PropTypes.object),
  favoriteVenueIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  fetchVenuesForCollection: PropTypes.func.isRequired,
  homepageModule: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
  loadNotifyModal: PropTypes.func.isRequired,
  locationCode: PropTypes.string,
  locationSlug: PropTypes.string,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  ResyEvents: PropTypes.shape({
    eventsByLocation: PropTypes.func.isRequired,
  }).isRequired,
  selectedDate: PropTypes.string,
  selectedSeats: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedTime: PropTypes.string,
  showAuthModal: PropTypes.func.isRequired,
  showSeeMore: PropTypes.bool,
  userIsGda: PropTypes.bool.isRequired,
  venues: PropTypes.shape(PropTypes.object.isRequired).isRequired,
};

const RGAListContainer = ({
  $rootScope,
  collectionList,
  fetchVenuesForCollection,
  isMobile,
  matchMedia,
  loadNotifyModal,
  locationCode,
  locationSlug,
  selectedDate,
  selectedSeats,
  selectedTime,
  showSeeMore,
  homepageModule,
  Analytics,
  venues,
  ResyEvents,
  isAuthenticated,
  showAuthModal,
  favoriteVenueIds,
  userIsGda,
}) => {
  const [events, setEvents] = useState([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [hasNoEventResults, setHasNoEventResults] = useState(true);
  const [isInView, setIsInView] = useState(false);

  const parseCollections = () => {
    const collection = collectionList.filter((val) => val.info.type_id === GDA_TYPE_ID);
    if (collection.length > 0) return collection[0];
    return null;
  };

  const fetchVenues = () => {
    const collection = parseCollections();
    const { id: collectionId } = collection;

    const payload = {
      location_id: locationCode,
      collection_id: collectionId,
      day: selectedDate,
      time_filter: selectedTime === ALL_DAY ? undefined : moment(selectedTime, 'HHmm').format('HH:mm'),
      party_size: selectedSeats,
      limit: 19,
      offset: 1,
      finder: 4,
      isAuth: isAuthenticated,
    };

    const key = `${locationCode}-${collectionId}-${selectedDate}-${selectedTime}-${selectedSeats}-${isAuthenticated}`;
    const venuesById = venues[key];

    if (!venuesById?.length) {
      try {
        fetchVenuesForCollection(payload);
      } catch (error) {
        Logger.error('[RGAListContainer] Error', error);
      }
    }
  };

  const fetchEvents = async () => {
    const startDate = new Date().toISOString().split('T')[0];

    const payload = {
      limit: EVENT_CARD_LIMIT,
      locationId: locationCode,
      startDate,
      filter_exclusive: 1000,
    };

    try {
      const eventList = await ResyEvents.eventsByLocation(payload);

      const {
        results,
        metadata: {
          total,
        },
      } = eventList;

      setEvents(results.events);
      setTotalEvents(total);
      setHasNoEventResults(!results.events.length);
    } catch (error) {
      setEvents([]);
      setTotalEvents(0);
      setHasNoEventResults(true);
    }
  };

  const getData = () => {
    fetchVenues();
    fetchEvents();
  };

  useEffect(() => {
    if (isInView && collectionList.length > 0) {
      getData();
    }
  }, [
    collectionList,
    isInView,
    locationCode,
    selectedDate,
    selectedSeats,
    selectedTime,
    isAuthenticated,
  ]);

  const getTitle = () => getTranslation(
    {
      translationKey: 'gda_title',
      fallback: 'Unlock Exclusive Tables with American Express',
    }
  );

  const onLearnMoreClicked = () => {
    window.open('/global-dining-access', '_blank');
  };

  const collection = parseCollections();
  if (!collection) return null;

  const {
    info: {
      image: badge,
    },
    id: collectionId,
  } = collection;

  const key = `${locationCode}-${collectionId}-${selectedDate}-${selectedTime}-${selectedSeats}-${isAuthenticated}`;
  const venuesById = venues[key];
  const hasNoVenueResults = !venuesById?.length;
  const eyebrow = getTranslation({ translationKey: 'gda_eyebrow', fallback: 'Resy Global Dining Access' });

  const description = (
    <div className="RGAListContainer__description">
      <TranslationProvider translationKey="gda_description">
        <span>
          We reserve tables exclusively for eligible American Express
          <sup>®</sup>
          Card Members.
        </span>
      </TranslationProvider>
        &nbsp;
      <Link
        target="_blank"
        variant="inline"
        onClick={onLearnMoreClicked}
      >
        <TranslationProvider translationKey="gda_description_learn_more">
          <span>
            Learn More
          </span>
        </TranslationProvider>
      </Link>
    </div>
  );

  const renderEvents = () => {
    if (hasNoEventResults) {
      return null;
    }

    if (hasNoVenueResults && venuesById?.length !== 0) {
      return (
        <>
          <div className="RGAListContainer__events--headline">
            <div className="container">
              <CollectionHeadline
                badge={badge}
                title={getTitle()}
                description={description}
                eyebrow={eyebrow}
                isGda
              />
              <Spacer height={10} />
              <TranslationProvider translationKey="upcoming_events">
                <h2 className="RGAListContainer__events--title">Upcoming Events</h2>
              </TranslationProvider>
            </div>
          </div>
          <EventList
            className="RGAListContainer__event-list"
            events={events}
            totalEvents={totalEvents}
            locationCode={locationCode}
            homepageModule={homepageModule}
            isGda
            maxCards={EVENT_CARD_LIMIT}
            disableHeadline
          />
        </>
      );
    }
    return (
      <>
        <div className="RGAListContainer__events--headline">
          <div className="container">
            <TranslationProvider translationKey="upcoming_events">
              <h2 className="RGAListContainer__events--title">Upcoming Events</h2>
            </TranslationProvider>
          </div>
        </div>
        <EventList
          className="RGAListContainer__event-list"
          events={events}
          totalEvents={totalEvents}
          locationCode={locationCode}
          disableHeadline
          homepageModule={homepageModule}
          isGda
          maxCards={homepageModule ? EVENT_CARD_LIMIT : undefined}
        />
      </>
    );
  };

  return (
    <InView onChange={(inView) => inView && setIsInView(true)}>
      {({ ref }) => (
        <div ref={ref} className="RGAListContainer">
          <div className="RGAListContainer__venues">
            {(!venuesById) ? (
              <VenueListSkeleton />
            ) : (
              <VenueList
                $rootScope={$rootScope}
                badge={badge}
                collectionSlug="global-dining-access"
                description={description}
                event={Analytics.event}
                eyebrow={eyebrow}
                favoriteVenueIds={favoriteVenueIds}
                hasNoResults={hasNoVenueResults}
                isGda
                isMobile={isMobile}
                listId={collectionId}
                loadNotifyModal={loadNotifyModal}
                locationCode={locationCode}
                locationSlug={locationSlug}
                matchMedia={matchMedia}
                maxCards={5}
                name={getTitle()}
                restaurants={venuesById}
                selectedDate={selectedDate}
                selectedSeats={selectedSeats}
                showAuthModal={showAuthModal}
                showSeeMore={showSeeMore}
                userIsGda={userIsGda}
              />
            )}
          </div>
          <div className="RGAListContainer__events">
            {renderEvents()}
          </div>
        </div>
      )}
    </InView>
  );
};

RGAListContainer.propTypes = rgaListContainerPropTypes;

RGAListContainer.defaultProps = {
  locationCode: 'ny',
  locationSlug: 'new-york-ny',
  selectedDate: null,
  selectedTime: ALL_DAY,
  selectedSeats: 2,
  showSeeMore: true,
  homepageModule: true,
  collectionList: [],
  isAuthenticated: false,
};

export const mapStateToProps = (state, props) => {
  const { locationCode, isAuthenticated } = props;
  const key = `${locationCode}-1-${isAuthenticated}`;
  const { collections } = state;
  return ({
    collectionList: collections.lists?.all?.[key]?.algorithmic,
    venues: collections.venues.other,
    userIsGda: selectIsGlobalDiningAccess(state),
  });
};

RGAListContainer.displayName = 'RGAListContainer';
export { RGAListContainer as ComponentUnderTest, rgaListContainerPropTypes };
export default connect(mapStateToProps)(RGAListContainer);
